/**
 * The live connection to the finance system, as the platform keeps it.
 *
 * `sap.ts` is the read — the sign, the reversals, the unmapped accounts, the
 * plan. This is the connection that feeds it on the platform
 * (backend/api/slt/finance/): where SAP is, how the platform signs in, which
 * company codes it reads, when, and what the last read found. The shapes are
 * the platform's own answers, under the names its router gives them.
 *
 * The secret is not here and never will be. The platform says whether one is
 * set and how — named in its environment, or entered once and sealed — and
 * nothing else; a screen that could show it would be a screen that could
 * leak it.
 */
import type { AccountRule, JournalLine, Rejection, SapRead, Unbudgeted, Unmapped } from "./sap";

export type SapAuthMode = "basic" | "oauth_client_credentials";

/** Why a read or a test did not complete — each is a different conversation with the SAP team. */
export type SapFailure =
  | "not_configured"
  | "refused_url"
  | "unauthorised"
  | "forbidden"
  | "not_found"
  | "unreachable"
  | "server"
  | "bad_response"
  | "too_large"
  | "abandoned"
  | "internal";

export interface SapSecretState {
  set: boolean;
  /** "environment" — named, and read from the deployment; "stored" — entered once, sealed */
  source: "environment" | "stored" | null;
  /** the environment variable's NAME, where it is named; never the value */
  name: string | null;
  setAt: string | null;
}

export interface FinanceConnection {
  siteId: string;
  enabled: boolean;
  label: string | null;
  baseUrl: string;
  servicePath: string;
  planPath: string | null;
  authMode: SapAuthMode;
  username: string | null;
  clientId: string | null;
  tokenUrl: string | null;
  scope: string | null;
  sapClient: string | null;
  companyCodes: string[];
  currency: string | null;
  accountRules: AccountRule[];
  fiscalStartMonth: number;
  fiscalNamedFor: "start" | "end";
  scheduleMinutes: number | null;
  secret: SapSecretState;
  /** whether this deployment holds the key to keep a secret typed into the panel */
  canStoreSecret: boolean;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastStatus: "never" | "running" | "ok" | "failed";
  lastError: string | null;
  lastErrorKind: SapFailure | null;
  updatedAt: string | null;
  service: string;
}

/** What the panel sends. The secret fields are write-only: sent once, never read back. */
export interface FinanceConnectionInput {
  enabled?: boolean;
  label?: string | null;
  baseUrl: string;
  servicePath?: string | null;
  planPath?: string | null;
  authMode: SapAuthMode;
  username?: string | null;
  clientId?: string | null;
  tokenUrl?: string | null;
  scope?: string | null;
  sapClient?: string | null;
  companyCodes: string[];
  currency?: string | null;
  accountRules?: AccountRule[];
  fiscalStartMonth?: number;
  fiscalNamedFor?: "start" | "end";
  scheduleMinutes?: number | null;
  secret?: string;
  secretRef?: string;
  clearSecret?: boolean;
}

export interface FinanceTest {
  ok: boolean;
  service: string;
  records?: number;
  missing?: string[];
  kind?: SapFailure;
  status?: number | null;
  error?: string;
  says: string;
  ms?: number;
}

/** What a read found: the counts, the refusals by reason, the accounts no rule covers. */
export interface FinanceReport {
  lines?: number;
  mapped?: number;
  rejected?: { reason: Rejection; lines: number; amount: number }[];
  unmapped?: Unmapped[];
  planned?: boolean;
  unbudgeted?: Unbudgeted[];
  currency?: string;
  pages?: number;
  calls?: number;
  retries?: number;
  ms?: number;
  file?: string;
}

export interface FinanceRun {
  id: string;
  source: "sap" | "file";
  kind: "journal" | "ledger";
  trigger: "manual" | "schedule" | "file" | "remap";
  status: "running" | "ok" | "failed";
  fiscalYear: string | null;
  fiscalPeriod: string | null;
  fileName: string | null;
  startedAt: string;
  finishedAt: string | null;
  report: FinanceReport;
  error: string | null;
  errorKind: SapFailure | null;
  says: string | null;
}

export interface FinanceStatus {
  configured: boolean;
  enabled: boolean;
  label: string | null;
  companyCodes: string[];
  scheduleMinutes: number | null;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastStatus: FinanceConnection["lastStatus"];
  lastError: string | null;
  lastErrorKind: SapFailure | null;
  says: string | null;
  running: boolean;
  lastRun: FinanceRun | null;
  lastJournalRun: FinanceRun | null;
  service: string;
}

/** The platform's latest journal read, with the rules that placed its money. */
export interface FinanceLatest {
  read: (SapRead & { rules?: AccountRule[] }) | null;
  run: FinanceRun | null;
}

export interface FinanceImportResult {
  id: string;
  status: "ok" | "failed";
  kind: "journal" | "ledger";
  report: FinanceReport & { reconciliation?: { balanced: boolean } };
}

/** How often a schedule reads, in words the panel offers. */
export const SCHEDULES: { minutes: number | null; label: string }[] = [
  { minutes: null, label: "only when asked" },
  { minutes: 60, label: "every hour" },
  { minutes: 360, label: "every six hours" },
  { minutes: 1440, label: "once a day" },
];

/**
 * What a failure means, said once for the screen — the platform says the same
 * in its report, and this is the fallback where an answer carries only the kind.
 */
export const FAILURE_LABEL: Record<SapFailure, string> = {
  not_configured: "the connection is missing something it needs before it can call",
  refused_url: "the address is not one this deployment will call",
  unauthorised: "SAP refused the credentials (401): the user, password, client id or secret is wrong or expired",
  forbidden: "SAP accepted who we are and refused what we asked (403): the communication user lacks an authorisation",
  not_found:
    "SAP has no such service at that address (404): the service is not activated, or the path or sap-client is wrong",
  unreachable: "SAP could not be reached, after retrying",
  server: "SAP answered with a server error, after retrying",
  bad_response: "SAP answered with something that is not the journal",
  too_large: "the read is larger than one read is allowed to be; narrow it by period or company code",
  abandoned: "the read was abandoned: the process running it stopped before it finished",
  internal: "the platform failed while storing the read",
};

/**
 * A journal extract in SAP's own field names — a Fiori export or a download
 * of the same entity — as JournalLines, or null when the file is not one.
 *
 * The platform reads such a file the same way (finance/store.py
 * `_journal_csv`): it becomes the site's latest journal, as a live read
 * would. A flag written X or true is set, blank or false is not, and anything
 * else is read as set — a reversal flag nobody can read is excluded and
 * shown, not counted.
 */
export function readJournalCsv(text: string): JournalLine[] | null {
  const rows = text.replace(/^﻿/, "").split(/\r?\n/);
  const first = rows[0] ?? "";
  const delimiter = [",", ";", "\t"].reduce((a, b) => (first.split(b).length > first.split(a).length ? b : a));
  const cells = (line: string) => splitCells(line, delimiter);
  const headers = cells(first).map((h) => h.trim());
  if (!["GLAccount", "AmountInCompanyCodeCurrency", "DebitCreditCode"].every((h) => headers.includes(h))) return null;
  const flag = (v: string) => !["", "false", "0", "no"].includes(v.trim().toLowerCase());
  const out: JournalLine[] = [];
  for (const row of rows.slice(1)) {
    if (!row.trim()) continue;
    const values = cells(row);
    const line: Record<string, unknown> = {};
    headers.forEach((h, i) => {
      const v = (values[i] ?? "").trim();
      if (h === "IsReversal" || h === "IsReversed") line[h] = flag(v);
      else if (v !== "" || h === "AmountInCompanyCodeCurrency" || h === "DebitCreditCode") line[h] = v;
    });
    if (Object.values(line).some(Boolean)) out.push(line as JournalLine);
  }
  return out;
}

/** One CSV line on one delimiter, honouring quotes. */
function splitCells(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cur += c;
    } else if (c === '"') quoted = true;
    else if (c === delimiter) {
      out.push(cur);
      cur = "";
    } else cur += c;
  }
  out.push(cur);
  return out;
}

/** Company codes as somebody types them: "1710, 1720" or one per line. */
export function parseCompanyCodes(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean),
    ),
  ];
}
